import { useCallback, useEffect, useRef, useState } from "react";
import {
  addFavorite,
  listClientFormSubmissions,
  listFavorites,
  listRecentSubmissions,
  listTaxForms,
  removeFavorite,
} from "@/features/forms/api";
import type { FormSubmission, TaxFormDef } from "@/features/forms/types";
import { ApiError } from "@/lib/api";

interface UseClientFormsResult {
  taxForms: TaxFormDef[];
  submissions: FormSubmission[];
  /** Favorited tax-form ids (for the heart toggle + Favorites filter). */
  favorites: Set<string>;
  /** Form numbers recently opened/edited for this client (for the Recent filter). */
  recentFormNumbers: Set<string>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  /** Optimistically add/remove a favorite, persisting to the backend. */
  toggleFavorite: (taxFormId: string) => void;
}

/**
 * Loads the IRS-form registry, the client's submissions, the user's favorites,
 * and the client's recent forms together so the page can render each form with
 * its status/flags. Same race guards as `use-cases.ts`.
 */
export function useClientForms(clientId: string): UseClientFormsResult {
  const [taxForms, setTaxForms] = useState<TaxFormDef[]>([]);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [recentFormNumbers, setRecentFormNumbers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    Promise.all([
      listTaxForms(),
      listClientFormSubmissions(clientId),
      listFavorites(),
      listRecentSubmissions(clientId),
    ])
      .then(([forms, subs, favs, recent]) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setTaxForms(forms);
        setSubmissions(subs);
        setFavorites(new Set(favs.map((f) => f.taxForm)));
        setRecentFormNumbers(new Set(recent.map((s) => s.formNumber)));
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setTaxForms([]);
        setSubmissions([]);
        setFavorites(new Set());
        setRecentFormNumbers(new Set());
        setError(err instanceof ApiError ? err.message : "Failed to load forms.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, reloadKey]);

  const toggleFavorite = useCallback((taxFormId: string) => {
    let wasFavorite = false;
    setFavorites((prev) => {
      const next = new Set(prev);
      wasFavorite = next.has(taxFormId);
      if (wasFavorite) next.delete(taxFormId);
      else next.add(taxFormId);
      return next;
    });
    const request = wasFavorite ? removeFavorite(taxFormId) : addFavorite(taxFormId);
    request.catch(() => {
      // Roll back the optimistic change on failure.
      if (!mountedRef.current) return;
      setFavorites((prev) => {
        const next = new Set(prev);
        if (wasFavorite) next.add(taxFormId);
        else next.delete(taxFormId);
        return next;
      });
    });
  }, []);

  return {
    taxForms,
    submissions,
    favorites,
    recentFormNumbers,
    loading,
    error,
    refetch,
    toggleFavorite,
  };
}
